import React from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
} from '@mui/material'
import { H6 } from '../../../../../../layouts/TextStyles'
import CustomInput from '../../../../../../components/muiComponents/customInput/CustomInput'
import {
    requiredString,
} from '../../../../../../utils/validation'
import { lessonScheduleApiSlice } from '../../../../../../features/schedule/lessonScheduleSlice'
import { useParams } from 'react-router-dom'
import { tone } from '../../../../../../utils/uiTokens'

interface Props {
    open?: boolean
    handleClose?: () => void
}

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
    fullWidth: {
        width: '100%',
    },
}

interface UploadLinkFormValues {
    date: string
    link: string
}

const AddUploadLinkDialog: React.FC<Props> = ({
    open = false,
    handleClose = () => { },
}) => {
    const { formatMessage } = useIntl()
    const { lessonId } = useParams()

    const [createUploadLink, { isLoading: isCreating }] =
        lessonScheduleApiSlice.useCreateUploadLinkMutation()

    const initialValues: UploadLinkFormValues = {
        date: '',
        link: '',
    }

    const validationSchema = yup.object().shape({
        date: requiredString({ formatMessage }),
        link: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            await handleCreateUploadLink()
        },
    })

    const handleCreateUploadLink = async () => {
        try {
            await createUploadLink({
                lessonId: Number(lessonId),
                body: {
                    date: formik.values.date,
                    link: formik.values.link,
                },
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleClose()
        } catch (error) {
            toast.error(formatMessage({ id: 'saveFail' }))
        }
    }

    return (
        <Dialog
            maxWidth={'sm'}
            fullWidth
            open={open ?? false}
            onClose={handleClose}
        >
            <DialogTitle>
                {formatMessage({ id: 'add' })}
            </DialogTitle>

            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Box>
                        <Box mb={2} sx={{ width: '100%' }}>
                            <H6>
                                {formatMessage({ id: 'time' })}
                            </H6>

                            <CustomInput
                                id="date"
                                name="date"
                                type="date"
                                value={formik.values.date}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.date)}
                                helperText={formik.errors.date}
                                placeholder={formatMessage({
                                    id: 'date',
                                })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />

                            <FormHelperText sx={styles.error}>
                                {formik.touched.date &&
                                    formik.errors.date}
                            </FormHelperText>
                        </Box>

                        <Box mb={2} sx={{ width: '100%' }}>
                            <H6>
                                {formatMessage({ id: 'lessonLink' })}
                            </H6>

                            <CustomInput
                                id="link"
                                name="link"
                                value={formik.values.link}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.link)}
                                helperText={formik.errors.link}
                                placeholder={formatMessage({
                                    id: 'link',
                                }) || 'https://...'}
                                fullWidth
                            />

                            <FormHelperText sx={styles.error}>
                                {formik.touched.link &&
                                    formik.errors.link}
                            </FormHelperText>
                        </Box>
                    </Box>

                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            disabled={isCreating}
                        >
                            {formatMessage({ id: 'cancel' })}
                        </Button>
                        <Button
                            onClick={() => formik.handleSubmit()}
                            variant="contained"
                            disabled={isCreating}
                        >
                            {isCreating ? (
                                <CircularProgress
                                    size={20}
                                    style={{ marginRight: '8px' }}
                                />
                            ) : (
                                formatMessage({ id: 'add' })
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default AddUploadLinkDialog

